import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { RESET } from "jotai/utils";
import { useCallback, useMemo } from "react";

import { useSidebar } from "@skilitics/designer-ui-components";

import { selectedAspectAtom, selectedResponseIdAtom } from "./selectionAtoms.ts";
import type { AspectType } from "./types.ts";

export type SelectedNodeAspect = AspectType;

export const useSelectedAspect = () => {
  const [selectedAspect, setSelectedAspect] = useAtom(selectedAspectAtom);
  const { popOut, main } = useSidebar();

  const onSelectedAspectChanged = useCallback(
    (aspect: AspectType) => {
      setSelectedAspect(aspect || RESET);
      if (aspect === "triggers") {
        popOut.closePanel();
        main.closePanel();
      }
    },
    [main, popOut, setSelectedAspect],
  );
  return useMemo(
    () => [selectedAspect, onSelectedAspectChanged] as [SelectedNodeAspect, (aspect: AspectType) => void],
    [onSelectedAspectChanged, selectedAspect],
  );
};

export const useIsTriggersOpen = () =>
  useAtomValue(
    useMemo(() => atom((get) => get(selectedAspectAtom) === "triggers" && !!get(selectedResponseIdAtom)), []),
  );

export const useResetAspect = () => {
  const setSelectedAspect = useSetAtom(selectedAspectAtom);
  const setSelectedResponseId = useSetAtom(selectedResponseIdAtom);

  return useCallback(() => {
    setSelectedAspect(RESET);
    setSelectedResponseId(RESET);
  }, [setSelectedAspect, setSelectedResponseId]);
};

export const useSelectedResponseId = () => useAtomValue(selectedResponseIdAtom);

export const useSetSelectedResponseId = () => {
  const setSelectedResponseId = useSetAtom(selectedResponseIdAtom);
  return useCallback((id?: number) => setSelectedResponseId(id || RESET), [setSelectedResponseId]);
};

export const useNavigateToResponseId = () => {
  const setSelectedAspect = useSetAtom(selectedAspectAtom);
  const setSelectedResponseId = useSetAtom(selectedResponseIdAtom);
  return useCallback(
    (id?: number) => {
      setSelectedAspect("triggers");
      setSelectedResponseId(id);
    },
    [setSelectedAspect, setSelectedResponseId],
  );
};
